<!-- src/components/layout/AppSidebar.vue -->
<template>
  <!-- Slim top bar, mobile only: houses the logo + menu trigger since there's no header -->
  <div v-if="isMobile" class="sidebar-mobile-topbar">
    <router-link to="/dashboard" class="sidebar-mobile-topbar-logo">
      <img src="/logo/LogoIcon.png" alt="Applypa" />
    </router-link>
    <button
      type="button"
      class="sidebar-mobile-topbar-trigger"
      aria-label="Open menu"
      @click="openMobile"
    >
      <i class="fas fa-bars"></i>
    </button>
  </div>

  <!-- Mobile overlay backdrop -->
  <div v-if="isMobileOpen" class="app-sidebar-backdrop" @click="closeMobile"></div>

  <aside
    class="app-sidebar"
    :class="{
      'is-expanded': isExpanded,
      'is-mobile-open': isMobileOpen,
    }"
    @mouseenter="!isMobile && onEnter()"
    @mouseleave="!isMobile && onLeave()"
  >
    <div class="sidebar-logo">
      <!-- Unpinned (desktop): the logo doubles as the "keep open" toggle and
           swaps to a sidebar icon on hover, like Claude's sidebar. -->
      <button
        v-if="!isMobile && !isPinned"
        type="button"
        class="sidebar-logo-link sidebar-logo-toggle"
        aria-label="Keep sidebar open"
        title="Keep sidebar open"
        @click="togglePin"
      >
        <span class="sidebar-logo-badge">
          <img src="/logo/LogoIcon.png" alt="Applypa" />
          <span class="sidebar-logo-toggle-icon">
            <svg viewBox="0 0 20 20" width="20" height="20" fill="none" stroke="currentColor" stroke-width="1.6" aria-hidden="true">
              <rect x="2.5" y="3.5" width="15" height="13" rx="2.5" />
              <line x1="7.5" y1="3.5" x2="7.5" y2="16.5" />
            </svg>
          </span>
        </span>
      </button>
      <router-link v-else to="/dashboard" class="sidebar-logo-link">
        <span class="sidebar-logo-badge">
          <img src="/logo/LogoIcon.png" alt="Applypa" />
        </span>
      </router-link>
      <button
        v-if="!isMobile && isPinned"
        type="button"
        class="sidebar-pin-btn"
        aria-label="Close sidebar"
        title="Close sidebar"
        @click="togglePin"
      >
        <svg viewBox="0 0 20 20" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.6" aria-hidden="true">
          <rect x="2.5" y="3.5" width="15" height="13" rx="2.5" />
          <line x1="7.5" y1="3.5" x2="7.5" y2="16.5" />
        </svg>
      </button>
    </div>

    <div v-if="isMobile" class="sidebar-mobile-header">
      <span class="sidebar-label">Menu</span>
      <div class="sidebar-mobile-header-actions">
        <button
          type="button"
          class="sidebar-close-btn"
          aria-label="Toggle theme"
          @click="toggleTheme"
        >
          <i :class="theme === 'dark' ? 'fas fa-sun' : 'fas fa-moon'"></i>
        </button>
        <button class="sidebar-close-btn" @click="closeMobile">
          <i class="fas fa-times"></i>
        </button>
      </div>
    </div>

    <nav class="sidebar-nav">
      <a
        v-for="item in visibleNavItems"
        :key="item.to"
        class="sidebar-link"
        :class="{ 'sidebar-link--active': isActive(item.to) }"
        href="#"
        @click.prevent="handleNavClick(item.to)"
      >
        <i :class="item.icon"></i>
        <span class="sidebar-label">{{ item.label }}</span>
      </a>
    </nav>

    <div v-if="isImpersonating" class="sidebar-viewas-banner" title="Viewing as this user">
      <span class="sidebar-viewas-icon"><i class="fas fa-eye"></i></span>
      <span class="sidebar-label sidebar-viewas-text">Viewing</span>
      <button type="button" class="sidebar-viewas-btn" title="Back to Admin" @click="returnToAdmin">
        <i class="fas fa-arrow-left"></i>
        <span class="sidebar-label">Admin</span>
      </button>
    </div>

    <div class="sidebar-footer">
      <a
        class="sidebar-link"
        :class="{ 'sidebar-link--active': isActive('/contact-us') }"
        href="#"
        @click.prevent="handleNavClick('/contact-us')"
      >
        <i class="fas fa-circle-question"></i>
        <span class="sidebar-label">Help</span>
      </a>
      <template v-if="isAdminUser">
        <a class="sidebar-link" href="#" @click.prevent="handleNavClick('/admin-dashboard')">
          <i class="fas fa-chart-line"></i>
          <span class="sidebar-label">Admin</span>
        </a>
        <a class="sidebar-link" :href="djangoAdminUrl" target="_blank" rel="noopener noreferrer">
          <i class="fas fa-database"></i>
          <span class="sidebar-label">Django</span>
        </a>
      </template>
    </div>

    <!-- Profile (bottom, ChatGPT-style) -->
    <div class="sidebar-profile">
      <div class="sidebar-profile-row">
        <button
          ref="profileBtnRef"
          type="button"
          class="sidebar-profile-btn"
          @click="toggleProfileMenu"
          aria-label="Profile menu"
          :aria-expanded="showProfileMenu ? 'true' : 'false'"
        >
          <span class="sidebar-profile-avatar">
            <img
              v-if="user && user.profile_picture"
              :src="user.profile_picture"
              :alt="getUserInitials()"
              @error="handleImageError"
            />
            <span v-else>{{ getUserInitials() }}</span>
          </span>
          <span class="sidebar-profile-info sidebar-label">
            <span class="sidebar-profile-name">{{ userDisplayName }}</span>
            <span
              class="sidebar-profile-subtitle"
              :class="subscriptionColorClass"
            >
              {{ subscriptionName }}
            </span>
          </span>
        </button>
        <button
          v-if="!isPremiumPlan"
          type="button"
          class="sidebar-profile-upgrade-pill sidebar-label"
          @click="goToUpgrade"
        >
          Upgrade
        </button>
      </div>
    </div>
  </aside>

  <teleport to="body">
    <div v-if="showProfileMenu" class="sidebar-profile-backdrop" @click="closeProfileMenu"></div>
    <div v-if="showProfileMenu" class="sidebar-profile-menu" :style="profileMenuStyle">
      <div class="sidebar-profile-menu-header">
        <p class="sidebar-profile-menu-name">{{ userDisplayName }}</p>
        <p class="sidebar-profile-menu-email">{{ user ? user.email : '' }}</p>
      </div>

      <div class="sidebar-profile-menu-items">
        <router-link
          v-if="!isPremiumPlan"
          to="/subscription/plans"
          class="sidebar-profile-menu-item sidebar-profile-menu-item--upgrade"
          @click="closeProfileMenu"
        >
          <i class="fas fa-arrow-up"></i> Upgrade plan
        </router-link>
        <router-link to="/profile" class="sidebar-profile-menu-item" @click="closeProfileMenu">
          <i class="fas fa-user-circle"></i> My Profile
        </router-link>
        <router-link to="/saved-jobs" class="sidebar-profile-menu-item" @click="closeProfileMenu">
          <i class="fas fa-bookmark"></i> Saved Jobs
        </router-link>
        <router-link to="/hidden-jobs" class="sidebar-profile-menu-item" @click="closeProfileMenu">
          <i class="fas fa-eye-slash"></i> Hidden Jobs
        </router-link>
        <router-link to="/applications" class="sidebar-profile-menu-item" @click="closeProfileMenu">
          <i class="fas fa-clipboard-list"></i> Applications
        </router-link>
        <router-link to="/settings" class="sidebar-profile-menu-item" @click="closeProfileMenu">
          <i class="fas fa-cog"></i> Settings
        </router-link>
        <button v-if="!isMobile" type="button" class="sidebar-profile-menu-item" @click="toggleTheme">
          <i :class="theme === 'dark' ? 'fas fa-sun' : 'fas fa-moon'"></i>
          {{ theme === 'dark' ? 'Light mode' : 'Dark mode' }}
        </button>
      </div>

      <div class="sidebar-profile-menu-footer">
        <button
          v-if="isImpersonating"
          type="button"
          class="sidebar-profile-menu-item"
          @click="returnToAdmin"
        >
          <i class="fas fa-arrow-left"></i> Back to Admin
        </button>
        <button type="button" class="sidebar-profile-menu-item sidebar-profile-menu-item--danger" @click="handleLogoutClick">
          <i class="fas fa-sign-out-alt"></i> Logout
        </button>
      </div>
    </div>
  </teleport>
</template>

<script>
import { computed, ref, onMounted, onBeforeUnmount, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import authStore from "@/store/auth";
import authService from "@/services/auth";
import { useLogout } from "@/composables/useLogout";
import { useTheme } from "@/composables/useTheme";

export default {
  name: "AppSidebar",
  emits: ["sidebar-change", "mobile-open-change"],

  setup(props, { emit, expose }) {
    const route = useRoute();
    const router = useRouter();
    const { logout } = useLogout();
    const { theme, toggleTheme } = useTheme();

    const navItems = [
      { label: "Dashboard", to: "/dashboard", icon: "fas fa-chart-pie" },
      { label: "Job Filters", to: "/job-filters", icon: "fas fa-sliders" },
      { label: "AI Instructions", to: "/ai-instructions", icon: "fas fa-wand-magic-sparkles" },
      { label: "Notifications", to: "/notifications", icon: "fas fa-bell" },
      { label: "Auto Apply", to: "/auto-apply", icon: "fas fa-robot" },
    ];

    const user = computed(() => authStore.state.user);
    const isAdminUser = computed(() => {
      return (
        user.value &&
        (user.value.is_superuser ||
          user.value.account_type === "admin" ||
          user.value.admin_profile)
      );
    });
    const oljUnlocked = computed(() => !!user.value?.olj_jobs_enabled);

    const djangoAdminUrl = computed(() => {
      const apiBase = process.env.VUE_APP_API_URL || `${window.location.origin}/api/`;
      return new URL("/admin/", apiBase).href;
    });

    // Auto Apply is hard-gated by olj_jobs_enabled (see the requiresOlj route
    // guard), so only surface it in the nav when the user actually has access.
    const visibleNavItems = computed(() =>
      navItems.filter((item) => item.to !== "/auto-apply" || oljUnlocked.value)
    );

    const userDisplayName = computed(() => {
      if (!user.value) return "User";
      return `${user.value.first_name || ""} ${user.value.last_name || ""}`.trim() || "User";
    });

    const getUserInitials = () => {
      if (!user.value || !user.value.first_name) return "?";
      const firstInitial = user.value.first_name.charAt(0);
      const lastInitial = user.value.last_name ? user.value.last_name.charAt(0) : "";
      return (firstInitial + lastInitial).toUpperCase();
    };

    const handleImageError = (event) => {
      event.target.style.display = "none";
    };

    // A lapsed paid/trial plan is treated as Free (the backend applies Free
    // limits right away, before the expiry cron moves the subscription).
    const effectivePlanType = computed(() => {
      const u = user.value;
      const type = u?.subscription?.plan?.plan_type;
      if (!type) return null;
      return u.has_active_subscription === false ? "free" : type;
    });

    const isOnTrial = computed(() => {
      return !!(
        user.value &&
        (user.value.is_trial_active || user.value.subscription?.status === "trial")
      );
    });

    const subscriptionName = computed(() => {
      if (!user.value || !user.value.subscription || !user.value.subscription.plan) {
        return "No Subscription";
      }
      if (effectivePlanType.value !== "free" && isOnTrial.value) return "Free Trial";
      switch (effectivePlanType.value) {
        case "premium":
          return "Premium";
        case "basic":
          return "Basic";
        case "free":
          return "Free";
        default:
          return "Unknown";
      }
    });

    const subscriptionColorClass = computed(() => {
      if (!user.value || !user.value.subscription || !user.value.subscription.plan) {
        return "";
      }
      if (effectivePlanType.value !== "free" && isOnTrial.value) return "sidebar-profile-subtitle--trial";
      switch (effectivePlanType.value) {
        case "premium":
          return "sidebar-profile-subtitle--premium";
        case "basic":
          return "sidebar-profile-subtitle--basic";
        case "free":
          return "sidebar-profile-subtitle--free";
        default:
          return "";
      }
    });

    const goToUpgrade = () => {
      closeProfileMenu();
      router.push("/subscription/plans");
    };

    const isPremiumPlan = computed(() => {
      return !!(
        user.value &&
        user.value.subscription &&
        user.value.subscription.plan &&
        user.value.subscription.plan.plan_type === "premium" &&
        !isOnTrial.value
      );
    });

    // ── Profile dropdown (opens upward, teleported to body) ─────────────────
    const profileBtnRef = ref(null);
    const showProfileMenu = ref(false);
    const profileMenuStyle = ref({});

    function openProfileMenu() {
      const el = profileBtnRef.value;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      profileMenuStyle.value = {
        left: `${Math.round(rect.left)}px`,
        bottom: `${Math.round(window.innerHeight - rect.top) + 8}px`,
      };
      showProfileMenu.value = true;
    }

    function closeProfileMenu() {
      showProfileMenu.value = false;
    }

    function toggleProfileMenu() {
      if (showProfileMenu.value) {
        closeProfileMenu();
      } else {
        openProfileMenu();
      }
    }

    function handleLogoutClick() {
      closeProfileMenu();
      logout();
    }

    // ── Impersonation (admin "login as user") ────────────────────────────
    const isImpersonating = ref(false);

    function syncImpersonationState() {
      isImpersonating.value = localStorage.getItem("is_impersonating") === "true";
    }

    async function returnToAdmin() {
      const adminToken = localStorage.getItem("admin_token");
      if (!adminToken) return;
      authService.setAuthToken(adminToken);
      localStorage.removeItem("admin_token");
      localStorage.removeItem("admin_user_snapshot");
      localStorage.removeItem("is_impersonating");
      syncImpersonationState();
      await authStore.checkAuthStatus();
      closeProfileMenu();
      closeMobile();
      router.push("/admin-dashboard");
    }

    watch(
      () => route.path,
      () => {
        closeProfileMenu();
        syncImpersonationState();
      },
    );

    function isActive(path) {
      return route.path === path || route.path.startsWith(path + "/");
    }

    // ── Desktop hover expand ───────────────────────────────────────────────
    const isHovered = ref(false);
    let enterTimer = null;
    let leaveTimer = null;

    const PIN_STORAGE_KEY = "sidebar_pinned";
    const isPinned = ref(localStorage.getItem(PIN_STORAGE_KEY) === "true");
    const isExpanded = computed(() => isHovered.value || isPinned.value);

    function onEnter() {
      clearTimeout(leaveTimer);
      enterTimer = setTimeout(() => {
        isHovered.value = true;
        emit("sidebar-change", true);
      }, 250);
    }

    function onLeave() {
      if (isPinned.value) return;
      clearTimeout(enterTimer);
      leaveTimer = setTimeout(() => {
        isHovered.value = false;
        emit("sidebar-change", false);
      }, 150);
    }

    function togglePin() {
      isPinned.value = !isPinned.value;
      localStorage.setItem(PIN_STORAGE_KEY, isPinned.value ? "true" : "false");
      if (isPinned.value) {
        clearTimeout(leaveTimer);
        isHovered.value = true;
        emit("sidebar-change", true);
      } else {
        onLeave();
      }
    }

    // ── Mobile drawer ──────────────────────────────────────────────────────
    const isMobile = ref(false);
    const isMobileOpen = ref(false);
    const isAnimating = ref(false);

    function checkMobile() {
      isMobile.value = window.innerWidth < 768;
    }

    function openMobile() {
      if (isMobileOpen.value) return;
      isMobileOpen.value = true;
      isAnimating.value = true;
      emit("mobile-open-change", true);
      document.body.style.overflow = "hidden";
      setTimeout(() => {
        isAnimating.value = false;
      }, 320);
    }

    function closeMobile() {
      isMobileOpen.value = false;
      isAnimating.value = false;
      emit("mobile-open-change", false);
      document.body.style.overflow = "";
    }

    function handleNavClick(to) {
      if (isAnimating.value) return;

      if (isMobile.value) {
        if (!isMobileOpen.value) return;
        closeMobile();
        setTimeout(() => router.push(to), 300);
        return;
      }

      if (isHovered.value && !isPinned.value) {
        clearTimeout(enterTimer);
        clearTimeout(leaveTimer);
        isHovered.value = false;
        emit("sidebar-change", false);
        setTimeout(() => router.push(to), 320);
      } else {
        router.push(to);
      }
    }

    function handleEscKey(event) {
      if (event.key === "Escape" && showProfileMenu.value) {
        closeProfileMenu();
      }
    }

    function handleWindowResize() {
      checkMobile();
      if (showProfileMenu.value) closeProfileMenu();
    }

    onMounted(() => {
      checkMobile();
      syncImpersonationState();
      window.addEventListener("resize", handleWindowResize);
      document.addEventListener("keydown", handleEscKey);
    });

    onBeforeUnmount(() => {
      window.removeEventListener("resize", handleWindowResize);
      document.removeEventListener("keydown", handleEscKey);
      document.body.style.overflow = "";
    });

    expose({ openMobile, closeMobile, isMobileOpen });

    return {
      navItems,
      visibleNavItems,
      isAdminUser,
      djangoAdminUrl,
      isActive,
      isHovered,
      isExpanded,
      isPinned,
      togglePin,
      isMobile,
      isMobileOpen,
      onEnter,
      onLeave,
      openMobile,
      closeMobile,
      handleNavClick,
      user,
      userDisplayName,
      getUserInitials,
      handleImageError,
      subscriptionName,
      subscriptionColorClass,
      isPremiumPlan,
      goToUpgrade,
      profileBtnRef,
      showProfileMenu,
      profileMenuStyle,
      toggleProfileMenu,
      closeProfileMenu,
      handleLogoutClick,
      theme,
      toggleTheme,
      isImpersonating,
      returnToAdmin,
    };
  },
};
</script>

<style scoped>
.sidebar-mobile-topbar {
  display: none;
}

@media (max-width: 767px) {
  .sidebar-mobile-topbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    height: 52px;
    padding: 0 10px 0 14px;
    background: hsl(var(--card));
    border-bottom: 1px solid hsl(var(--border));
    /* Above the hard paywall modal so the mobile menu trigger stays reachable. */
    z-index: 10000;
  }

  .sidebar-mobile-topbar-logo {
    display: flex;
    align-items: center;
  }

  .sidebar-mobile-topbar-logo img {
    width: 32px;
    height: 32px;
    border-radius: 8px;
    object-fit: cover;
  }

  .sidebar-mobile-topbar-trigger {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 38px;
    height: 38px;
    border: 1px solid hsl(var(--border));
    border-radius: 8px;
    background: hsl(var(--accent));
    color: hsl(var(--foreground));
    cursor: pointer;
  }
}

.app-sidebar-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  z-index: 149;
  animation: fadeIn 0.25s ease;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to   { opacity: 1; }
}

.app-sidebar {
  width: 56px;
  flex-shrink: 0;
  background: hsl(var(--card));
  border-right: 1px solid hsl(var(--border));
  display: flex;
  flex-direction: column;
  position: sticky;
  top: 0;
  height: 100vh;
  height: 100dvh;
  overflow: hidden;
  transition: width 0.35s cubic-bezier(0.4, 0, 0.2, 1);
  /* Stays above the hard paywall modal (z-[9999]) so the profile menu
     escape hatch (logout/upgrade/settings/help) remains reachable. */
  z-index: 10000;
}

.app-sidebar.is-expanded {
  width: 220px;
}

@media (max-width: 767px) {
  .app-sidebar {
    position: fixed !important;
    top: 0 !important;
    left: 0 !important;
    width: 260px !important;
    height: 100vh !important;
    height: 100dvh !important;
    transform: translateX(-100%) !important;
    transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
    z-index: 10000 !important;
  }

  .app-sidebar.is-mobile-open {
    transform: translateX(0) !important;
  }
}

.sidebar-logo {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.75rem 8px 0.5rem;
  flex-shrink: 0;
}

.sidebar-logo-link {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  text-decoration: none;
  border-radius: 8px;
  padding: 0.2rem;
}

.sidebar-logo-badge {
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  border-radius: 10px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
}

.sidebar-logo-badge img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: opacity 0.15s ease;
}

.sidebar-logo-toggle {
  border: none;
  background: transparent;
  cursor: pointer;
}

.sidebar-logo-toggle .sidebar-logo-badge {
  position: relative;
  transition: background 0.15s ease;
}

.sidebar-logo-toggle-icon {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: hsl(var(--foreground));
  opacity: 0;
  transition: opacity 0.15s ease;
}

.sidebar-logo-toggle:hover .sidebar-logo-badge,
.sidebar-logo-toggle:focus-visible .sidebar-logo-badge {
  background: hsl(var(--accent));
}

.sidebar-logo-toggle:hover img,
.sidebar-logo-toggle:focus-visible img {
  opacity: 0;
}

.sidebar-logo-toggle:hover .sidebar-logo-toggle-icon,
.sidebar-logo-toggle:focus-visible .sidebar-logo-toggle-icon {
  opacity: 1;
}

.sidebar-mobile-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem;
  border-bottom: 1px solid hsl(var(--border));
}

.sidebar-mobile-header .sidebar-label {
  font-size: 0.8rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: hsl(var(--muted-foreground));
  opacity: 1;
}

.sidebar-mobile-header-actions {
  display: flex;
  align-items: center;
  gap: 0.4rem;
}

.sidebar-close-btn {
  background: hsl(var(--accent));
  border: none;
  color: hsl(var(--muted-foreground));
  width: 28px;
  height: 28px;
  border-radius: 7px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8rem;
  transition: background 0.15s, color 0.15s;
}

.sidebar-close-btn:hover {
  background: hsl(var(--accent) / 0.7);
  color: hsl(var(--foreground));
}

.sidebar-pin-btn {
  width: 32px;
  height: 32px;
  flex-shrink: 0;
  border: none;
  border-radius: 7px;
  background: transparent;
  color: hsl(var(--muted-foreground));
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  pointer-events: none;
  transition: background 0.15s, color 0.15s, opacity 0.15s ease;
}

.is-expanded .sidebar-pin-btn {
  opacity: 1;
  pointer-events: auto;
  transition: opacity 0.2s ease 0.1s;
}

.sidebar-pin-btn:hover {
  background: hsl(var(--accent));
  color: hsl(var(--foreground));
}

.sidebar-nav {
  flex: 1;
  padding: 0.75rem 8px;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  overflow-y: auto;
  overflow-x: hidden;
}

.sidebar-link {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.6rem 10px;
  border-radius: 8px;
  color: hsl(var(--muted-foreground));
  text-decoration: none;
  font-size: 0.875rem;
  font-weight: 500;
  transition: background 0.15s, color 0.15s;
  white-space: nowrap;
  overflow: hidden;
  cursor: pointer;
}

.sidebar-link:hover {
  background: hsl(var(--accent));
  color: hsl(var(--foreground));
}

.sidebar-link--active {
  background: hsl(var(--primary) / 0.15);
  color: hsl(var(--primary));
}

.sidebar-link i:first-child {
  width: 20px;
  text-align: center;
  font-size: 0.85rem;
  flex-shrink: 0;
}

.sidebar-label {
  opacity: 0;
  transition: opacity 0.15s ease 0.05s;
  overflow: hidden;
}

.is-expanded .sidebar-label,
.is-mobile-open .sidebar-label {
  opacity: 1;
  transition: opacity 0.2s ease 0.1s;
}

@media (max-width: 767px) {
  .sidebar-label {
    opacity: 1 !important;
  }
}

.sidebar-footer {
  padding: 0.75rem 8px;
  border-top: 1px solid hsl(var(--border));
}

.sidebar-viewas-banner {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  margin-top: 0.6rem;
  padding: 0.5rem 8px;
  overflow: hidden;
  flex-shrink: 0;
  width: 100%;
}

.sidebar-viewas-icon {
  display: none;
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  background: hsl(var(--foreground) / 0.1);
  color: hsl(var(--foreground));
  font-size: 0.7rem;
}

.is-expanded .sidebar-viewas-icon,
.is-mobile-open .sidebar-viewas-icon {
  display: flex;
}

@media (max-width: 767px) {
  .sidebar-viewas-icon {
    display: flex;
  }
}

.sidebar-viewas-text {
  display: none;
  flex-shrink: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 0.75rem;
  font-weight: 600;
  color: hsl(var(--foreground));
}

.is-expanded .sidebar-viewas-text,
.is-mobile-open .sidebar-viewas-text {
  display: block;
}

@media (max-width: 767px) {
  .sidebar-viewas-text {
    display: block;
  }
}

.sidebar-viewas-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  flex: 1;
  min-width: 20px;
  border: 1.5px solid hsl(var(--foreground));
  border-radius: 999px;
  background: transparent;
  color: hsl(var(--foreground));
  padding: 0.35rem 0.8rem;
  font-size: 0.75rem;
  font-weight: 600;
  white-space: nowrap;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;
}

.sidebar-viewas-btn:hover {
  background: hsl(var(--accent));
}


/* ── Profile widget (bottom, ChatGPT-style) ────────────────────────────── */
.sidebar-profile {
  padding: 0.5rem 8px 0.75rem;
  border-top: 1px solid hsl(var(--border));
}

.sidebar-profile-btn {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.45rem;
  border: none;
  background: transparent;
  border-radius: 8px;
  cursor: pointer;
  text-align: left;
  transition: background 0.15s;
}

.sidebar-profile-btn:hover {
  background: hsl(var(--accent));
}

.sidebar-profile-avatar {
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  border-radius: 999px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background: hsl(var(--primary) / 0.15);
  color: hsl(var(--primary));
  font-size: 0.7rem;
  font-weight: 700;
}

.sidebar-profile-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.sidebar-profile-info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  line-height: 1.2;
}

.sidebar-profile-name {
  font-size: 0.8rem;
  font-weight: 600;
  color: hsl(var(--foreground));
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.sidebar-profile-subtitle {
  font-size: 0.7rem;
  font-weight: 600;
  color: hsl(var(--muted-foreground));
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.sidebar-profile-subtitle--expired {
  color: #dc2626;
}

.sidebar-profile-subtitle--premium {
  color: #7c3aed;
}

.sidebar-profile-subtitle--basic {
  color: #2563eb;
}

.sidebar-profile-subtitle--free {
  color: #16a34a;
}

.sidebar-profile-subtitle--trial {
  color: #2563eb;
}

.sidebar-profile-row {
  display: flex;
  align-items: center;
  gap: 0.4rem;
}

.sidebar-profile-upgrade-pill {
  flex-shrink: 0;
  padding: 0.35rem 0.8rem;
  border-radius: 999px;
  border: 1px solid hsl(var(--border));
  background: transparent;
  color: hsl(var(--foreground));
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.15s, border-color 0.15s;
}

.sidebar-profile-upgrade-pill:hover {
  background: hsl(var(--accent));
  border-color: hsl(var(--primary) / 0.4);
}

/* Dropdown menu — teleported to body, opens upward, fixed-positioned */
.sidebar-profile-backdrop {
  position: fixed;
  inset: 0;
  /* Above the hard paywall modal so the profile dropdown stays reachable. */
  z-index: 10000;
}

.sidebar-profile-menu {
  position: fixed;
  width: 260px;
  border: 1px solid hsl(var(--border));
  border-radius: 12px;
  background: hsl(var(--popover));
  color: hsl(var(--popover-foreground));
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.18);
  overflow: hidden;
  z-index: 10001;
}

.sidebar-profile-menu-header {
  padding: 0.75rem 1rem;
  border-bottom: 1px solid hsl(var(--border));
  background: hsl(var(--muted) / 0.4);
}

.sidebar-profile-menu-name {
  font-size: 0.85rem;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.sidebar-profile-menu-email {
  font-size: 0.75rem;
  color: hsl(var(--muted-foreground));
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-top: 1px;
}

.sidebar-profile-menu-items {
  padding: 0.25rem;
}

.sidebar-profile-menu-footer {
  padding: 0.25rem;
  border-top: 1px solid hsl(var(--border));
}

.sidebar-profile-menu-item {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.5rem 0.75rem;
  border: none;
  background: transparent;
  border-radius: 8px;
  color: hsl(var(--foreground));
  font-size: 0.825rem;
  text-decoration: none;
  cursor: pointer;
  transition: background 0.15s;
}

.sidebar-profile-menu-item:hover {
  background: hsl(var(--accent));
}

.sidebar-profile-menu-item i {
  width: 16px;
  text-align: center;
  color: hsl(var(--muted-foreground));
  font-size: 0.8rem;
}

.sidebar-profile-menu-item--danger {
  color: hsl(var(--destructive));
}

.sidebar-profile-menu-item--danger i {
  color: hsl(var(--destructive));
}

.sidebar-profile-menu-item--upgrade {
  color: hsl(var(--primary));
  font-weight: 600;
  margin-bottom: 0.25rem;
  border-bottom: 1px solid hsl(var(--border));
  border-radius: 8px 8px 0 0;
}

.sidebar-profile-menu-item--upgrade i {
  color: hsl(var(--primary));
}

.sidebar-profile-menu-item--upgrade:hover {
  background: hsl(var(--primary) / 0.1);
}
</style>
